import {
  faEye,
  faEyeSlash,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams } from '@tanstack/react-router';
import { isAfter, parseISO } from 'date-fns';
import { FC, useMemo, useState } from 'react';
import ResponseBox from 'src/components/0100_response_box';
import useEvent from 'src/hooks/events/useEvent';
import useEventAttendee from 'src/hooks/events/useEventAttendee';
import useEventModSections from 'src/hooks/events/useEventModSections';
import usePermission from 'src/hooks/permissions/usePermissions';
import useRootUser from 'src/hooks/players/useRootUser';
import Basics from './Basics';
import Sections from './Sections';

const Mods: FC = () => {
  const { rootUserId } = useRootUser();
  const { event } = useEvent();
  const { organizationSlug } = useParams({ strict: false });
  const { isPermitted: canManageMod } = usePermission({
    action: 'assists_mod',
    eventId: Number(event?.id),
  });
  const [isAliasedView, setIsAliasedView] = useState(false);
  const aliasedCanManageMod = isAliasedView ? !canManageMod : canManageMod;
  const { eventAttendee } = useEventAttendee();

  const eventModMaxSignups = event?.modMaxSignup;
  const { modSections } = useEventModSections();
  const eventHasEnded = useMemo(
    () => (event?.endsAt ? isAfter(new Date(), parseISO(event.endsAt)) : true),
    [event?.endsAt],
  );
  const modSignupIsOpen = useMemo(
    () =>
      event?.modSignupOpensAt
        ? isAfter(new Date(), parseISO(event.modSignupOpensAt))
        : false,
    [event?.modSignupOpensAt],
  );
  const eventModSignupsCount = useMemo(
    () =>
      (modSections ?? [])
        .map(
          x =>
            x.mods.filter(x =>
              x.modSignups.some(y => y.eventAttendee.user.id === rootUserId),
            ).length,
        )
        .reduce((a, b) => a + b, 0),
    [modSections, rootUserId],
  );
  const eventAllowsMoreSignup = useMemo(() => {
    if (!eventModMaxSignups) return true;

    return eventModSignupsCount < eventModMaxSignups;
  }, [eventModMaxSignups, eventModSignupsCount]);

  return (
    <div className="flex justify-center w-full">
      <div className="grid gap-4 max-w-[1024px] w-full place-items-center">
        {canManageMod && (
          <div className="flex justify-end w-full">
            <button
              type="button"
              className="gray-box border border-juno-gray-700 rounded hover:brightness-125 p-2 px-4 transition-all duration-300 flex items-center gap-2"
              onClick={() => setIsAliasedView(x => !x)}
            >
              <FontAwesomeIcon
                icon={isAliasedView ? faEye : faEyeSlash}
                className="fa-fw"
              />
              Switch to {isAliasedView ? 'Mod Manager' : 'Player'} view
            </button>
          </div>
        )}
        <Basics
          canManageMod={aliasedCanManageMod}
          modSignupIsOpen={modSignupIsOpen}
          eventHasEnded={eventHasEnded}
          eventModSignupsCount={eventModSignupsCount}
        />

        {eventAttendee || canManageMod ? (
          <Sections
            canManageMod={aliasedCanManageMod}
            eventAllowsMoreSignup={eventAllowsMoreSignup}
            eventHasEnded={eventHasEnded}
            modSignupIsOpen={modSignupIsOpen}
            waitlistAllowed={event?.modEnableWaitlist ?? false}
          />
        ) : (
          <div className="w-full">
            <ResponseBox type="error" withIcon={faTriangleExclamation}>
              Please{' '}
              <Link
                to="/$organizationSlug/events/$eventId"
                className="underline"
                params={{
                  organizationSlug: String(organizationSlug),
                  eventId: String(event?.id),
                }}
              >
                Checkin
              </Link>{' '}
              first prior to signing up for Mods.
            </ResponseBox>
          </div>
        )}
      </div>
    </div>
  );
};

export default Mods;
